import React, { useState, useEffect, useRef } from 'react'
import { useForecast } from '../../hooks/useForecast'
import { C, F, DEPTS } from './theme'
import { Card, SectionHead, Btn, DeptChip, Avatar, Bar, Head, Icon } from './prims'
import type { ForecastPhase } from '../../types'
import { useForecastConflicts } from './useForecastConflicts'
import { ChangeNoticePanel } from './ChangeNoticePanel'
import { ApprovalTeamPanel } from './ApprovalTeamPanel'

// EM-7 — Team & approval. Assemble the delivery team from the Functions
// library, cycle each function's approval state, and lay out the delivery
// timeline as a draggable Gantt over T-40w → 0. Every P&L-free tab: guards on
// fc === null and persists through the useForecast mutations.

const MAX = 40
const TICKS = [40, 30, 20, 10, 0]
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))
const fmtDays = (ds: string[]) => {
  const f = (s: string) => { const p = s.split('-'); return `${Number(p[2])}/${Number(p[1])}` }
  return ds.length <= 3 ? ds.map(f).join(', ') : `${f(ds[0]!)}–${f(ds[ds.length - 1]!)}`
}

type ApprovalStatus = 'pending' | 'approved' | 'blocked'
const NEXT: Record<ApprovalStatus, ApprovalStatus> = { pending: 'approved', approved: 'blocked', blocked: 'pending' }
const APPROVAL_STYLE: Record<ApprovalStatus, { bg: string; fg: string; label: string }> = {
  pending: { bg: C.amberSoft, fg: C.amber, label: 'Pending' },
  approved: { bg: C.greenBg, fg: C.green, label: 'Approved' },
  blocked: { bg: C.oxbloodSoft, fg: C.oxblood, label: 'Blocked' }
}

export function TeamTab({ eventId }: { eventId: number }) {
  const { fc, ev, patchMember, removeMember, savePhases } = useForecast(eventId)

  // Local Gantt phases, kept in sync with the server copy. phasesRef mirrors
  // the latest local phases so the drag-end save reads them without threading a
  // network call through a state updater (which would double-fire in StrictMode).
  const [phases, setPhases] = useState<ForecastPhase[]>([])
  const phasesRef = useRef<ForecastPhase[]>([])
  const applyPhases = (next: ForecastPhase[]) => { phasesRef.current = next; setPhases(next) }
  useEffect(() => { if (fc) applyPhases(fc.phases) }, [fc])

  const trackRef = useRef<HTMLDivElement | null>(null)
  const { conflicts, total: conflictCount } = useForecastConflicts(eventId, fc?.proposed.dates ?? [])

  if (!fc) return null

  const team = fc.team
  const total = team.length
  const approved = team.filter((t) => t.approvalStatus === 'approved').length
  const pending = team.filter((t) => t.approvalStatus === 'pending').length
  const blocked = team.filter((t) => t.approvalStatus === 'blocked').length
  const pct = total > 0 ? (approved / total) * 100 : 0

  const cycleApproval = (rowId: number, cur: ApprovalStatus) => {
    patchMember(rowId, { approvalStatus: NEXT[cur] }).catch(() => {})
  }

  // ---- Gantt drag ----------------------------------------------------------
  const onPhaseDown = (e: React.MouseEvent, p: ForecastPhase) => {
    const track = trackRef.current
    if (!track) return
    const startX = e.clientX
    const s0 = p.startWeeksBefore
    const e0 = p.endWeeksBefore
    const dur = s0 - e0
    const pxPerWeek = track.offsetWidth / MAX

    const onMove = (ev: MouseEvent) => {
      const dWeeks = Math.round((ev.clientX - startX) / pxPerWeek)
      const newStart = clamp(s0 - dWeeks, dur, MAX)
      const newEnd = newStart - dur
      applyPhases(phasesRef.current.map((q) => q.id === p.id ? { ...q, startWeeksBefore: newStart, endWeeksBefore: newEnd } : q))
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      savePhases(phasesRef.current.map((q) => ({ label: q.label, dept: q.dept, startWeeksBefore: q.startWeeksBefore, endWeeksBefore: q.endWeeksBefore, ownerPersonId: q.ownerPersonId }))).catch(() => {})
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  return (
    <div>
      <Head
        eyebrow="Delivery · approval"
        title="Team & approval"
        right={
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="secondary" small icon="book" onClick={() => { /* Sprint */ }}>Save to library</Btn>
            <Btn variant="primary" small icon="send" onClick={() => { /* Sprint */ }}>Circulate</Btn>
          </div>
        }
      />

      {/* Event conflicts on the proposed dates */}
      {conflictCount > 0 && (
        <Card style={{ marginBottom: 16, borderColor: C.amber, background: C.amberSoft }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <Icon name="alert" size={16} color={C.amber} />
            <span style={{ fontFamily: F.serif, fontSize: 15, fontWeight: 800, color: C.ink }}>
              {conflictCount} event {conflictCount === 1 ? 'clash' : 'clashes'} on the proposed dates
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {conflicts.map(({ event, overlapDates }) => (
              <div key={event.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: F.body, fontSize: 9.5, fontWeight: 700, color: event.type.colourText, background: event.type.colourBg, border: `1px solid ${event.type.colourBorder}`, borderRadius: 4, padding: '1px 6px' }}>{event.type.name}</span>
                <span style={{ fontFamily: F.body, fontSize: 13, fontWeight: 700, color: C.ink }}>{event.name}</span>
                <span style={{ fontFamily: F.mono, fontSize: 11, color: C.inkSoft }}>{overlapDates.length} day{overlapDates.length === 1 ? '' : 's'} · {fmtDays(overlapDates)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Change notice — auto-raised (title = idea name), formal sign-off,
          synced to The Big Plan on commit */}
      <ChangeNoticePanel eventId={eventId} defaultTitle={ev?.name} />

      {/* 1) Approval progress bar */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: F.serif, fontSize: 18, fontWeight: 800, color: C.ink }}>
            {approved} of {total} functions approved
          </div>
          <div style={{ marginLeft: 'auto', fontFamily: F.body, fontSize: 12.5, fontWeight: 700, color: C.inkSoft }}>
            Approved {approved} · Pending {pending} · Blocked {blocked}
          </div>
        </div>
        <Bar pct={pct} color={C.green} h={10} />
      </Card>

      {/* 2) Delivery team roster (assignment happens in the Approval Team panel below) */}
      <Card style={{ marginBottom: 16 }}>
        <SectionHead icon="users" label="Delivery team" right={<span style={{ fontFamily: F.body, fontSize: 11.5, color: C.inkFaint }}>Assign people below ↓</span>} />

        {team.length === 0 ? (
          <div style={{ fontFamily: F.body, fontSize: 13, color: C.inkFaint, padding: '10px 2px' }}>
            No delivery functions assigned yet — use “The Approval Team for this Idea” below.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {team.map((row) => {
              const ap = APPROVAL_STYLE[row.approvalStatus]
              return (
                <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '10px 12px', borderRadius: 10, border: `1px solid ${C.lineSoft}`, background: C.paper }}>
                  <Avatar name={row.personName} size={34} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontFamily: F.serif, fontSize: 15, fontWeight: 700, color: C.ink }}>{row.personName}</span>
                      <DeptChip id={row.dept} small />
                    </div>
                    <div style={{ fontFamily: F.body, fontSize: 12.5, color: C.inkSoft, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.functionTitle}
                      {row.commitmentText ? <span style={{ color: C.inkFaint }}> · {row.commitmentText}</span> : null}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => cycleApproval(row.id, row.approvalStatus)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 999, background: ap.bg, color: ap.fg, fontFamily: F.body, fontSize: 11.5, fontWeight: 800, border: `1px solid ${ap.fg}33`, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    <Icon name="check" size={12} />{ap.label}
                  </button>
                  <Btn variant="ghost" small icon="x" onClick={() => removeMember(row.id).catch(() => {})}>Remove</Btn>
                </div>
              )
            })}
          </div>
        )}
      </Card>

      {/* 3) Delivery timeline (draggable Gantt) */}
      <Card>
        <SectionHead icon="calendar" label="Delivery timeline" />

        <div
          ref={trackRef}
          data-track
          style={{ position: 'relative', minHeight: Math.max(60, phases.length * 42 + 12), padding: '6px 0' }}
        >
          {/* grid lines */}
          {TICKS.map((t) => (
            <div
              key={`grid-${t}`}
              style={{ position: 'absolute', top: 0, bottom: 0, left: `${((MAX - t) / MAX) * 100}%`, width: 1, background: C.lineSoft }}
            />
          ))}

          {/* phase bars */}
          {phases.map((p, i) => {
            const d = DEPTS[p.dept] ?? DEPTS.events!
            const left = ((MAX - p.startWeeksBefore) / MAX) * 100
            const width = ((p.startWeeksBefore - p.endWeeksBefore) / MAX) * 100
            return (
              <div
                key={p.id}
                onMouseDown={(e) => onPhaseDown(e, p)}
                style={{
                  position: 'absolute',
                  top: i * 42 + 6,
                  left: `${left}%`,
                  width: `${width}%`,
                  height: 30,
                  background: d.bg,
                  border: `1px solid ${d.fg}44`,
                  borderLeft: `3px solid ${d.fg}`,
                  borderRadius: 7,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '0 8px',
                  cursor: 'grab',
                  userSelect: 'none',
                  overflow: 'hidden'
                }}
              >
                <span style={{ width: 7, height: 7, borderRadius: 4, background: d.fg, flexShrink: 0 }} />
                <span style={{ fontFamily: F.body, fontSize: 11.5, fontWeight: 800, color: d.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.label}</span>
              </div>
            )
          })}
        </div>

        {/* tick labels */}
        <div style={{ position: 'relative', height: 18, marginTop: 4 }}>
          {TICKS.map((t) => (
            <span
              key={`tick-${t}`}
              style={{ position: 'absolute', left: `${((MAX - t) / MAX) * 100}%`, transform: 'translateX(-50%)', fontFamily: F.mono, fontSize: 10.5, fontWeight: 700, color: C.inkFaint, whiteSpace: 'nowrap' }}
            >
              {t === 0 ? 'Event' : `T-${t}`}
            </span>
          ))}
        </div>
      </Card>

      {/* 4) The Approval Team for this Idea — function-block assignment surface */}
      <ApprovalTeamPanel eventId={eventId} />
    </div>
  )
}
